/**
 * Cova Feed — the broker's spotlight RPC contract (spec cova-feed/2026-10-09-001
 * §5.2): the queue, the seen record, "Ask your admin", and the coach mark.
 *
 * ITS OWN GROUP, not more methods on FeedRpc, because it needs
 * `EntitlementsMiddleware` (the caller's resolved `{ tier, features }` as
 * `CallerEntitlementsContext`) to resolve each spotlight's gate, and adding
 * that middleware to FeedRpc would put an entitlements resolution on every feed
 * read.
 *
 * Every payload names a post, never an org or a user: the caller comes from the
 * resolved identity. `surface` only narrows which spotlights are eligible; it
 * grants nothing.
 *
 * FRONTEND-SAFE: pg-free, so the broker host's client imports it.
 */
import { Rpc, RpcGroup } from "@effect/rpc"
import { AllowedApps } from "@/presentation/rpc/middleware/allowed-apps"
import { Schema as S } from "effect"
import { AuthorisationError } from "@/application/shared/schemas/authorisation-schemas"
import { UserMiddleware } from "@/presentation/rpc/middleware/user-middleware"
import { RequestIdentityMiddleware } from "@/presentation/rpc/middleware/request-identity-middleware"
import { EntitlementsMiddleware } from "@/presentation/rpc/middleware/entitlements-middleware"
import { WireErrorSanitizerMiddleware } from "@/shared/error-diagnostics/wire"
import { FeedPostId } from "../domain/feed-enums"
import { SpotlightCoachMark, SpotlightQueue, SpotlightScope } from "../domain/spotlight/spotlight-settings"
import { FeedSpotlightWireError } from "./feed-spotlight-wire-error"

const SPOTLIGHT_ERROR = S.Union(AuthorisationError, FeedSpotlightWireError)
const Ok = S.Struct({ ok: S.Literal(true) })

/** At most 5, newest first, each with its button resolved for this caller. */
export const GetSpotlightQueueRpc = Rpc.make("getSpotlightQueue", {
  payload: S.Struct({ surface: SpotlightScope }),
  success: SpotlightQueue,
  error: SPOTLIGHT_ERROR,
})

/** `requested` is not here: only `requestFeatureAccess` writes it, after the admin is told. */
export const MarkSpotlightSeenRpc = Rpc.make("markSpotlightSeen", {
  payload: S.Struct({
    postId: FeedPostId,
    version: S.Int,
    outcome: S.Literal("dismissed", "opened", "upgraded"),
  }),
  success: Ok,
  error: SPOTLIGHT_ERROR,
})

/** Keyed on the post: a broker can only ask for what a live spotlight offered them. */
export const RequestFeatureAccessRpc = Rpc.make("requestFeatureAccess", {
  payload: S.Struct({ postId: FeedPostId, version: S.Int }),
  success: S.Struct({ delivered: S.Boolean }),
  error: SPOTLIGHT_ERROR,
})

/** Non-null only for the caller's own opened row at the current version, while undismissed. */
export const GetSpotlightCoachMarkRpc = Rpc.make("getSpotlightCoachMark", {
  payload: S.Struct({ postId: FeedPostId }),
  success: SpotlightCoachMark,
  error: SPOTLIGHT_ERROR,
})

export const DismissSpotlightCoachMarkRpc = Rpc.make("dismissSpotlightCoachMark", {
  payload: S.Struct({ postId: FeedPostId, version: S.Int }),
  success: Ok,
  error: SPOTLIGHT_ERROR,
})

// `.middleware()` covers only the RPCs added before the call, so the sanitizer
// goes LAST.
export const FeedSpotlightRpc = RpcGroup.make(
  GetSpotlightQueueRpc,
  MarkSpotlightSeenRpc,
  RequestFeatureAccessRpc,
  GetSpotlightCoachMarkRpc,
  DismissSpotlightCoachMarkRpc,
)
  .annotateRpcs(AllowedApps, ["platform"])
  .middleware(UserMiddleware)
  .middleware(RequestIdentityMiddleware)
  .middleware(EntitlementsMiddleware)
  .middleware(WireErrorSanitizerMiddleware)

export { FeedSpotlightWireError } from "./feed-spotlight-wire-error"
